import "../css/header.scss";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import { productCategories, siteConfig } from "../Datas/productData";

function Header() {
  const { cartCount, wishlist } = useContext(CartContext);
  const location = useLocation();
  const navigate = useNavigate();
  const searchInput = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const isCheckout = ["/checkout", "/payment"].includes(location.pathname);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    window.scrollTo({ top: 0, behavior: "auto" });

    const titleByPath = {
      "/": "Regenerative textiles",
      "/shop": "All products",
      "/about": "Our story",
      "/contact": "Contact",
      "/cartpage": "Shopping cart",
      "/checkout": "Secure checkout",
      "/privacy": "Privacy policy",
      "/terms": "Terms & conditions",
      "/returns": "Returns",
      "/shipping": "Shipping",
      "/faqs": "FAQs",
      "/cookies": "Cookie policy",
      "/policies": "Policy center",
      "/payment": "Payment status",
    };
    const pageTitle = location.pathname.startsWith("/shopDetails/")
      ? "Product"
      : location.pathname.startsWith("/policies/")
        ? "Store policy"
        : titleByPath[location.pathname] || "Storefront";
    document.title = `${pageTitle} · ${siteConfig.brandName}`;

    const description = document.querySelector('meta[name="description"]');
    if (description) description.setAttribute("content", siteConfig.description);
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen) searchInput.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    const closeWithEscape = (event) => {
      if (event.key === "Escape") {
        setSearchOpen(false);
        setMobileOpen(false);
      }
    };
    window.addEventListener("keydown", closeWithEscape);
    return () => window.removeEventListener("keydown", closeWithEscape);
  }, []);

  const handleSearch = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();
    navigate(query ? `/shop?search=${encodeURIComponent(query)}` : "/shop");
  };

  if (isCheckout) {
    return (
      <header className="checkout-header">
        <Link to="/" className="checkout-header__logo" aria-label={`${siteConfig.brandName} home`}>
          <img src={siteConfig.logo} alt={siteConfig.brandName} />
        </Link>
        <span className="checkout-header__secure">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7 10V7a5 5 0 0 1 10 0v3M5 10h14v11H5z" />
          </svg>
          Secure checkout
        </span>
      </header>
    );
  }

  return (
    <>
      <div className="announcement-bar">
        <Link to="/shop">{siteConfig.announcement}</Link>
      </div>

      <header className="site-header">
        <div className="site-header__inner">
          <nav className="desktop-nav" aria-label="Primary navigation">
            <div className="desktop-nav__dropdown">
              <NavLink to="/shop" className="desktop-nav__link">
                Products
              </NavLink>
              <div className="desktop-nav__menu">
                <div>
                  <span className="desktop-nav__eyebrow">Shop by collection</span>
                  {productCategories.slice(1).map((category) => (
                    <Link
                      key={category}
                      to={`/shop?category=${encodeURIComponent(category)}`}
                    >
                      {category}
                    </Link>
                  ))}
                </div>
                <div className="desktop-nav__feature">
                  <span>Made on demand</span>
                  <p>Better materials, considered quantities, longer use.</p>
                  <Link to="/about">Explore our approach →</Link>
                </div>
              </div>
            </div>
            <NavLink to="/about" className="desktop-nav__link">
              Story
            </NavLink>
            <div className="desktop-nav__dropdown">
              <span className="desktop-nav__link" tabIndex="0">More</span>
              <div className="desktop-nav__menu">
                <div>
                  <span className="desktop-nav__eyebrow">Information</span>
                  <Link to="/policies">Policy center</Link><Link to="/privacy">Privacy policy</Link><Link to="/cookies">Cookie policy</Link><Link to="/terms">Terms & conditions</Link><Link to="/shipping">Shipping</Link><Link to="/returns">Returns</Link><Link to="/faqs">FAQs</Link>
                </div>
              </div>
            </div>
            <NavLink to="/contact" className="desktop-nav__link">
              Contact
            </NavLink>
          </nav>

          <div className="mobile-actions">
            <button
              type="button"
              className="header-icon"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
              aria-expanded={mobileOpen}
            >
              <svg viewBox="0 0 24 18" aria-hidden="true">
                <path d="M1 1h22M1 9h22M1 17h22" />
              </svg>
            </button>
            <button
              type="button"
              className="header-icon"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="10.5" cy="10.5" r="7.5" />
                <path d="m16 16 6 6" />
              </svg>
            </button>
          </div>

          <Link to="/" className="site-header__logo" aria-label={`${siteConfig.brandName} home`}>
            <img src={siteConfig.logo} alt={siteConfig.brandName} />
          </Link>

          <nav className="header-utilities" aria-label="Store utilities">
            <span className="header-language">English</span>
            <button
              type="button"
              className="header-text-link"
              onClick={() => setSearchOpen(true)}
            >
              Search
            </button>
            <Link className="header-text-link wishlist-link" to="/shop?wishlist=1">
              Wishlist <span>{wishlist.length}</span>
            </Link>
            <Link className="header-text-link cart-link" to="/cartpage">
              Cart <span>{cartCount}</span>
            </Link>
          </nav>
        </div>
      </header>

      <div
        className={`search-drawer ${searchOpen ? "is-open" : ""}`}
        aria-hidden={!searchOpen}
      >
        <button
          className="drawer-overlay"
          aria-label="Close search"
          onClick={() => setSearchOpen(false)}
        />
        <div className="search-drawer__panel">
          <form onSubmit={handleSearch} className="search-drawer__form">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="10.5" cy="10.5" r="7.5" />
              <path d="m16 16 6 6" />
            </svg>
            <label htmlFor="site-search" className="sr-only">
              Search products
            </label>
            <input
              ref={searchInput}
              id="site-search"
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search products, materials, collections…"
              autoComplete="off"
            />
            <button type="submit">Search</button>
            <button
              type="button"
              className="search-drawer__close"
              onClick={() => setSearchOpen(false)}
              aria-label="Close search"
            >
              ×
            </button>
          </form>
          <div className="search-drawer__suggestions">
            <span>Popular</span>
            {["Organic cotton", "Merino", "Home & Living", "Trail pants"].map(
              (term) => (
                <button
                  key={term}
                  type="button"
                  onClick={() => {
                    setSearchTerm(term);
                    navigate(`/shop?search=${encodeURIComponent(term)}`);
                  }}
                >
                  {term}
                </button>
              )
            )}
          </div>
        </div>
      </div>

      <div
        className={`mobile-menu ${mobileOpen ? "is-open" : ""}`}
        aria-hidden={!mobileOpen}
      >
        <button
          className="drawer-overlay"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
        <aside className="mobile-menu__panel" aria-label="Mobile navigation">
          <div className="mobile-menu__header">
            <span>Navigation</span>
            <button onClick={() => setMobileOpen(false)} aria-label="Close navigation">
              ×
            </button>
          </div>
          <div className="mobile-menu__links">
            <Link to="/shop" className="mobile-menu__main-link">
              All products
            </Link>
            {productCategories.slice(1).map((category) => (
              <Link
                key={category}
                to={`/shop?category=${encodeURIComponent(category)}`}
              >
                {category}
              </Link>
            ))}
            <Link to="/about" className="mobile-menu__main-link">
              Our story
            </Link>
            <Link to="/contact" className="mobile-menu__main-link">
              Contact
            </Link>
            <Link to="/policies">Policy center</Link><Link to="/privacy">Privacy policy</Link><Link to="/cookies">Cookie policy</Link><Link to="/terms">Terms & conditions</Link><Link to="/shipping">Shipping</Link><Link to="/returns">Returns</Link><Link to="/faqs">FAQs</Link>
            <Link to="/shop?wishlist=1">Wishlist ({wishlist.length})</Link>
          </div>
          <p className="mobile-menu__note">{siteConfig.description}</p>
        </aside>
      </div>
    </>
  );
}

export default Header;
